import { useCallback, useRef, useState, useMemo, MouseEvent as IMouseEvent, useEffect } from 'react';
import { scale } from '@components/frame';
import { localTime, machineStatusColorName } from '@helpers';
import { Popup } from '@components/machines/components/timeline';
import { MachineEventSearchItem } from '@interfaces/machines';
import { getMachineEventTimelineByBatchId } from '@reducers/machines';
import { useFocusEffect } from '@react-navigation/native';
import { WebViewMessageEvent } from 'react-native-webview';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { useRealtimeTimelineUpdates } from '@components/machines/components/timeline/hooks/useRealtimeTimelineUpdates';
import { BatchTimeLineTrackProps } from '@v2/pages/batch/details/modules/batch/components/BatchTimeLineTrack/BatchTimeLineTrack';
import { toMachineEventSearchItem } from '@components/wallboard/helpers/batchTimeline';

export interface UseManageBatchTimeLineTrackParams extends Pick<BatchTimeLineTrackProps, 'C' | 'height' | 'borderRadius' | 'start' | 'end' | 'setEnd' | 'productionBatchId' | 'roundOnlyBottom' | 'roundOnlyTop' | 'timelineRef' | 'triggerHidePopup' | 'triggerShowPopup' | 'handleMouseMove' | 'setEvents' | 'events' | 'withGaps'> {}

export const useManageBatchTimeLineTrack = ({
  C,
  height = 6,
  borderRadius = 4,
  start,
  end,
  setEnd,
  productionBatchId,
  roundOnlyBottom,
  roundOnlyTop,
  timelineRef,
  triggerHidePopup,
  triggerShowPopup,
  handleMouseMove,
  setEvents,
  events,
  withGaps = true,
}: UseManageBatchTimeLineTrackParams) => {
  const { fetchTimelineWithRealtime } = useRealtimeTimelineUpdates();

  const webView = useRef(null);
  const abortSignal = useRef<AbortController>();
  const [machineEventData, setMachineEventData] = useState<MachineEventSearchItem[]>([]);
  const machineEventDataRef = useRef(machineEventData);
  machineEventDataRef.current = machineEventData;
  const isMountedRef = useRef(true);
  const prevProductionBatchIdRef = useRef<number | string | undefined>(productionBatchId);
  const [isLoading, setIsLoading] = useState(false);

  const containerBg = C('Background/Dark/Chart');
  const timelineRect = timelineRef ? timelineRef?.current?.getBoundingClientRect() : null;

  const calculatedStart = start || machineEventData[0]?.startedOn;
  const calculatedEnd = end || machineEventData?.at(-1)?.endedOn;

  const totalDurationInMs =
    calculatedStart && calculatedEnd ? Math.abs(localTime(calculatedStart).diff(localTime(calculatedEnd), 'milliseconds')) : 0;

  useEffect(() => {
    if (!events) return;
    setMachineEventData(events);
    setIsLoading(false);
  }, [events]);

  useEffect(() => {
    setMachineEventData([]);
    setIsLoading(true);
  }, [productionBatchId]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      if (abortSignal.current) {
        abortSignal.current.abort();
        abortSignal.current = undefined;
      }
    };
  }, []);

  useFocusEffect(
    useCallback(() => {
      if (productionBatchId == null || !!events) return;

      const batchIdChanged = productionBatchId != null && prevProductionBatchIdRef.current !== productionBatchId;
      if (batchIdChanged) {
        setIsLoading(true);
        prevProductionBatchIdRef.current = productionBatchId;
      }

      if (abortSignal.current) {
        abortSignal.current.abort();
      }
      abortSignal.current = new AbortController();

      const handleEvents = (machineEvents: MachineEventSearchItem[]) => {
        if (!isMountedRef.current) return;
        setMachineEventData(machineEvents);
        setEvents?.(machineEvents);
        setIsLoading(false);
        if (machineEvents.length > 0 && setEnd) {
          setEnd?.(localTime(machineEvents.at(-1)?.endedOn)?.format());
        }
      };

      const cancelRealtime = fetchTimelineWithRealtime({
        productionBatchId,
        thunk: getMachineEventTimelineByBatchId(
          { productionBatchId, useAsyncFlowForRealtimeQuery: true, pagination: { pageNumber: 1, pageSize: 1000, includeCount: true } },
          abortSignal.current.signal
        ),
        processResponse: (machineEvents) => machineEvents.map(toMachineEventSearchItem),
        onInitialData: handleEvents,
        onRealtimeData: handleEvents,
        getCurrentEvents: () => ({
          loaded: true,
          events: machineEventDataRef.current as any,
          groupedByNCFile: [],
          groupedByBatch: [],
        }),
      });

      return () => {
        cancelRealtime();
        if (abortSignal.current) {
          abortSignal.current.abort();
        }
      };
    }, [productionBatchId])
  );

  const processedEvents = useMemo(() => {
    if (machineEventData.length === 0) return [];
    return [...machineEventData].sort((a, b) => localTime(a.startedOn).valueOf() - localTime(b.startedOn).valueOf());
  }, [machineEventData]);

  const startTime = calculatedStart ? localTime(calculatedStart) : localTime();

  const radiusStyle = {
    ...(roundOnlyTop
      ? {
          borderTopLeftRadius: scale(borderRadius),
          borderTopRightRadius: scale(borderRadius),
        }
      : roundOnlyBottom
      ? {
          borderBottomLeftRadius: scale(borderRadius),
          borderBottomRightRadius: scale(borderRadius),
        }
      : {
          borderRadius: scale(borderRadius),
        }),
  };

  const containerBorderRadiusStyle = `
  ${roundOnlyTop ? `border-top-left-radius: ${borderRadius}px; border-top-right-radius: ${borderRadius}px;` : ''}
  ${roundOnlyBottom ? `border-bottom-left-radius: ${borderRadius}px; border-bottom-right-radius: ${borderRadius}px;` : ''}
  ${!roundOnlyTop && !roundOnlyBottom ? `border-radius: ${borderRadius}px;` : ''}
`;

  const handleMessage = useCallback(
    (event: WebViewMessageEvent) => {
      if (!isMountedRef.current) return;

      if (event.nativeEvent.data?.source || typeof event.nativeEvent.data !== 'string') {
        // to avoid react-dev-tools crash because it sends messages into WebView
        return;
      }

      try {
        const data = JSON.parse(event.nativeEvent.data);
        if (data.type === 'showPopup' && triggerShowPopup && timelineRect) {
          triggerShowPopup(
            {
              show: true,
              item: processedEvents?.[+data.payload.index],
              width: data.payload.width,
              position: {
                top: timelineRef?.current?.top - data.payload.position.top + 50,
                left: data.payload.position.left,
              },
            },
            'productivityStatus'
          );
        } else if (data.type === 'hidePopup' && triggerHidePopup) {
          triggerHidePopup('productivityStatus');
        } else if (data.type === 'mouseMove') {
          if (!handleMouseMove) return;
          const timelineRect = timelineRef?.current?.getBoundingClientRect();

          const syntheticEvent = new MouseEvent(data.payload.eventType, {
            clientX: data.payload.clientX + (timelineRect?.left || 0),
            clientY: data.payload.clientY + (timelineRect?.top || 0),
            bubbles: true,
          });
          handleMouseMove(syntheticEvent);
        }
      } catch (e) {
        console.error(e);
      }
    },
    [triggerShowPopup, triggerHidePopup, handleMouseMove, timelineRect, processedEvents, timelineRef]
  );

  const generateHtml = useCallback(() => {
    let htmlEvents = '';

    if (!isLoading && processedEvents.length > 0) {
      let cumulativeWidth = 0;
      htmlEvents = processedEvents
        .map((event, index) => {
          const started = localTime(event.startedOn);
          const ended = localTime(event.endedOn);
          const eventDuration = ended.diff(started);
          const offsetFromStart = started.diff(startTime);

          let leftPercent;
          const widthPercent = (eventDuration / totalDurationInMs) * 100;

          if (withGaps) {
            leftPercent = (offsetFromStart / totalDurationInMs) * 100;
          } else {
            leftPercent = cumulativeWidth;
            cumulativeWidth += widthPercent;
          }

          const color = machineStatusColorName?.[event.status] || containerBg;

          return `
          <div
            class="batch-event-block"
            id="${index}"
            style="
              position: absolute;
              left: ${leftPercent}%;
              width: ${widthPercent}%;
              height: 100%;
              background-color: ${C(color)};
              border-top-left-radius: ${index === 0 && !roundOnlyBottom ? borderRadius : 0}px;
              border-bottom-left-radius: ${index === 0 && !roundOnlyTop ? borderRadius : 0}px;
            ">
          </div>
        `;
        })
        .join('');
    }

    return `
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="UTF-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <style>
            body, html {
              margin: 0;
              padding: 0;
              height: ${height}px;
              width: 100%;
              overflow: hidden;
            }
            .timeline-container {
              position: relative;
              width: 100%;
              height: ${height}px;
              ${containerBorderRadiusStyle}
              background: ${containerBg};
              overflow: hidden;
            }
          </style>
        </head>
        <body>
          <div class="timeline-container">
            ${htmlEvents}
          </div>
        </body>
       <script>
        let isDestroyed = false;

        if (window.ReactNativeWebView) {
          window.addEventListener('beforeunload', function() {
            isDestroyed = true;
          });
        }

        function sendMessage(type, payloadData = {}) {
          if (isDestroyed) return;

          const message = {
            type,
            payload: payloadData,
          };

          const payload = JSON.stringify(message);

          if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
            // React Native WebView
            window.ReactNativeWebView.postMessage(payload);
          } else if (window.opener) {
            // Web new window
            window.opener.postMessage(payload, window.opener.origin);
            window.close();
          } else if (window.parent) {
            // Web iframe
            window.parent.postMessage(payload, window.parent.origin);
          } else {
            // React Native fallback
            if (typeof onBridgeReady === 'function') {
              onBridgeReady(function () {
                window.postMessage(payload);
              });
            }
          }
        }

        const timelineContainer = document.getElementsByClassName('timeline-container')?.[0];

        const onMouseMove = function (e) {
          if (isDestroyed) return;
          sendMessage('mouseMove', {eventType: e.type, clientX: e.clientX, clientY: e.clientY});
        }

        if (timelineContainer) {
          timelineContainer.addEventListener('mousemove', onMouseMove);

          timelineContainer.addEventListener('mouseover', function (event) {
            if (isDestroyed) return;

            const targetElement = event.target;
            if (targetElement.classList.contains('batch-event-block')) {
              const el = targetElement;

              const width = el.offsetWidth;
              const rect = el.getBoundingClientRect();

              sendMessage('showPopup', {
                width,
                index: el.id,
                position: {
                  top: rect.top,
                  left: rect.left,
                },
              });

              const onMouseLeave = function () {
                if (isDestroyed) return;
                sendMessage('hidePopup');
                el.removeEventListener('mouseleave', onMouseLeave);
              };

              el.addEventListener('mouseleave', onMouseLeave);
            }
          });
        }
      </script>
      </html>
    `;
  }, [
    isLoading,
    processedEvents,
    height,
    containerBorderRadiusStyle,
    containerBg,
    totalDurationInMs,
    startTime,
    C,
    borderRadius,
    roundOnlyBottom,
    roundOnlyTop,
    withGaps,
  ]);

  return {
    webView,
    handleMessage,
    generateHtml,
    radiusStyle,
    height,
  };
};
